import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { setFechaPagoRecurrente, eliminarFechaPagoRecurrente } from "../../utils/proveedoresRecurrentes";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// Override de la fecha de pago de UNA ocurrencia de un proveedor recurrente en la proyección
// (Movimientos Futuros → "Recurrentes"). Escribe en pago_recurrente_fecha (upsert por la UNIQUE
// proveedor_id/periodo_año/periodo_mes) o borra el override (→ vuelve a la fecha derivada de dia_pago).
// Solo mueve la FECHA proyectada de ese pago puntual; el monto no se toca. Molde: EditarFechaSueldoModal.

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const formatFechaLarga = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [yyyy, mm, dd] = f.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const EditarFechaRecurrenteModal = ({ show, onHide, entry, pais, userId, onSaved }) => {
  const [fecha, setFecha] = useState(entry?.fecha_display || "");
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  const periodoLabel = `${MESES[entry.periodo_mes - 1]} ${entry.periodo_año}`;

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("Seleccioná una fecha");
      return;
    }
    setGuardando(true);
    try {
      await setFechaPagoRecurrente(
        entry.proveedor_id,
        entry.periodo_año,
        entry.periodo_mes,
        fecha,
        pais,
        userId,
        entry.costo_asociado_id ?? null
      );
      toast.success("Fecha del pago actualizada");
      // Patch puntual (Fase 2A): el padre reubica la ocurrencia recurrente por su clave compuesta.
      onSaved?.({ nuevaFecha: fecha, tieneOverride: true });
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleRestaurar = async () => {
    setGuardando(true);
    try {
      await eliminarFechaPagoRecurrente(
        entry.proveedor_id,
        entry.periodo_año,
        entry.periodo_mes,
        entry.costo_asociado_id ?? null
      );
      toast.success("Restaurada a la fecha original");
      // Revert: vuelve a la fecha derivada del día de pago (fecha_default del entry). override = false.
      onSaved?.({ nuevaFecha: entry.fecha_default, tieneOverride: false });
    } catch (e) {
      toast.error(`Error al restaurar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Correr la fecha de este pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Proveedor:</strong> {entry.razon_social_receptor || "—"}
          </div>
          <div>
            <strong>Período:</strong> {periodoLabel}
          </div>
          <div>
            <strong>Fecha original:</strong> {formatFechaLarga(entry.fecha_default)}
            <span style={{ color: "#888", marginLeft: 6, fontSize: "0.8rem" }}>(según día de pago)</span>
          </div>
          <div>
            <strong>Fecha efectiva actual:</strong> {formatFechaLarga(entry.fecha_display)}
            <span style={{ color: "#888", marginLeft: 6, fontSize: "0.8rem" }}>
              {entry.tiene_override ? "(override)" : "(sin override)"}
            </span>
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Nueva fecha de pago</Form.Label>
          <SelectorFechaDocumento
            value={fecha}
            onChange={(nuevaFecha) => setFecha(nuevaFecha || "")}
            pais={pais}
            modo={MODOS_VALIDACION.ESTRICTO}
            disabled={guardando}
          />
          <Form.Text style={{ color: "#888" }}>
            Solo cambia la fecha proyectada de este mes; el monto no se toca.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div>
          {entry.tiene_override && (
            <Button variant="outline-secondary" size="sm" onClick={handleRestaurar} disabled={guardando}>
              Restaurar fecha original
            </Button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
            Cerrar
          </Button>
          <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditarFechaRecurrenteModal;
